import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, FileText, AlertCircle, TrendingUp, Camera, CheckCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface AIQuickActionsProps {
  jobId: string;
  hasPhotos: boolean;
  hasMeasurements: boolean;
  hasEstimate: boolean;
  onNavigateToTab: (tab: string) => void;
}

export const AIQuickActions = ({
  jobId,
  hasPhotos,
  hasMeasurements,
  hasEstimate,
  onNavigateToTab,
}: AIQuickActionsProps) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleQuickAction = async (action: string) => {
    setIsProcessing(true);
    
    // Simulate AI processing
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    switch (action) {
      case 'check_missing':
        const missing = [];
        if (!hasPhotos) missing.push("photos");
        if (!hasMeasurements) missing.push("measurements");
        if (!hasEstimate) missing.push("estimate");
        
        if (missing.length > 0) {
          toast.info(`Missing: ${missing.join(", ")}`);
        } else {
          toast.success("All key data is present!");
        }
        break;
        
      case 'damage_summary':
        if (!hasPhotos) {
          toast.error("Upload photos first to analyze damage");
        } else {
          onNavigateToTab('Damage Hub');
        }
        break;
        
      case 'estimate_overview':
        if (!hasEstimate) {
          toast.info("No estimate found. Create one first.");
        } else {
          onNavigateToTab('Estimates');
        }
        break;
        
      default:
        toast.info("AI action processed");
    }
    
    setIsProcessing(false);
  };

  return (
    <Card className="p-6 border-primary/20 bg-gradient-to-br from-primary/5 via-card to-secondary/5">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center premium-glow">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-display font-bold">AI Quick Actions</h3>
          <p className="text-xs text-muted-foreground">Smart shortcuts for common tasks</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Button
          variant="outline"
          className="h-auto flex-col gap-2 py-4 border-primary/30 hover:bg-primary/10"
          onClick={() => handleQuickAction('check_missing')}
          disabled={isProcessing}
        >
          <AlertCircle className="w-5 h-5 text-primary" />
          <span className="text-sm font-medium">Check Missing</span>
          <span className="text-xs text-muted-foreground">Data</span>
        </Button>

        <Button
          variant="outline"
          className="h-auto flex-col gap-2 py-4 border-secondary/30 hover:bg-secondary/10"
          onClick={() => handleQuickAction('damage_summary')}
          disabled={isProcessing}
        >
          <Camera className="w-5 h-5 text-secondary" />
          <span className="text-sm font-medium">Damage</span>
          <span className="text-xs text-muted-foreground">Summary</span>
        </Button>

        <Button
          variant="outline"
          className="h-auto flex-col gap-2 py-4 border-accent/30 hover:bg-accent/10"
          onClick={() => handleQuickAction('estimate_overview')}
          disabled={isProcessing}
        >
          <FileText className="w-5 h-5 text-accent" />
          <span className="text-sm font-medium">Estimate</span>
          <span className="text-xs text-muted-foreground">Overview</span>
        </Button>

        <Button
          variant="outline"
          className="h-auto flex-col gap-2 py-4 border-success/30 hover:bg-success/10"
          onClick={() => onNavigateToTab('Tasks')}
          disabled={isProcessing}
        >
          <CheckCircle className="w-5 h-5 text-success" />
          <span className="text-sm font-medium">Next</span>
          <span className="text-xs text-muted-foreground">Steps</span>
        </Button>
      </div>
    </Card>
  );
};
